import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import "./AddImages.css";
import { Empty, Button } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import DateTimeRangePicker from "../../component/DatetimePicker";
import CardGallery from "../../component/CardGallery";
import { CameraViewCard, CameraViewItem } from "./CameraViewCard";

dayjs.extend(utc);
dayjs.extend(timezone);

interface CameraTriggerPair {
  camera: string;
  trigger: string;
}

const AddImagesCameraPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { outflowId } = useParams<{ outflowId: string }>();

  const system_name = searchParams.get("system_name") || "";

  // Existing states
  const [jobStart, setJobStart] = useState<Dayjs | null>(null);
  const [jobEnd, setJobEnd] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalOutflow, setTotalOutflow] = useState<number | null>(null);

  // New loading states for job dates and gallery images
  const [jobDatesLoading, setJobDatesLoading] = useState<boolean>(false);
  const [galleryLoading, setGalleryLoading] = useState<boolean>(false);

  // The gallery items we’ll display (with camera + trigger info)
  const [galleryImages, setGalleryImages] = useState<CameraViewItem[]>([]);
  // New state to capture any errors when fetching camera views
  const [galleryError, setGalleryError] = useState<string | null>(null);

  // Instead of selected IDs, store the actual camera/trigger pairs
  const [selectedPairs, setSelectedPairs] = useState<CameraTriggerPair[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '30', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch job dates (only once when outflowId or system_name change)
  useEffect(() => {
    if (!jobStart || !jobEnd) {
      fetchJobDates();
    }
  }, [outflowId, system_name]);

  // Fetch gallery images whenever outflowId, system_name, or currentPage changes
  useEffect(() => {
    fetchGalleryImages();
  }, [outflowId, system_name, currentPage]);

  const fetchJobDates = async () => {
    if (!outflowId || !system_name) return;
    setJobDatesLoading(true);
    try {
      // Run both fetch calls concurrently using Promise.all
      const [jobDatesResponse, outflowResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_URL}/api/outflow/${outflowId}/job-dates?system_name=${encodeURIComponent(system_name)}`
        ),
        fetch(
          `${process.env.REACT_APP_API_URL}/api/outflow/${outflowId}/outflow-total?system_name=${encodeURIComponent(system_name)}`
        )
      ]);
      
      if (!jobDatesResponse.ok || !outflowResponse.ok) {
        throw new Error("Failed to fetch job dates or outflow total.");
      }
      
      const jobDatesData = await jobDatesResponse.json();
      setJobStart(dayjs.tz(jobDatesData[0], "America/Chicago"));
      setJobEnd(dayjs.tz(jobDatesData[1], "America/Chicago"));
      
      const total = await outflowResponse.json();
      setTotalOutflow(total);
    } catch (error) {
      console.error("Error fetching job dates:", error);
    } finally {
      setJobDatesLoading(false);
    }
  };

  const fetchGalleryImages = async (page: number = 1, size: number = 30) => {
    if (!outflowId || !system_name) return;
    setGalleryLoading(true);
    // Reset any previous error
    setGalleryError(null);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
      });
      // Include pagination parameters in the API call
      const url = `${process.env.REACT_APP_API_URL}/api/teachmode/camera-views/${outflowId}?system_name=${encodeURIComponent(
        system_name
      )}&${queryParams.toString()}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch image list.");
      }
      const data = await response.json();
      console.log(data);
      const items = data.images.map((img: any) => {
          return {
            id: img.id,
            camera: img.camera,
            trigger: img.label,
            path: img.path,
            label: `Camera ${img.camera}, Trigger ${img.label}`,
          };
        });
      console.log(items);
      setGalleryImages(items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (error) {
      console.error("Error fetching images:", error);
      setGalleryError("No camera views found.");
    } finally {
      setGalleryLoading(false);
    }
  };

  const isSamePair = (a: CameraTriggerPair, b: CameraTriggerPair) =>
    a.camera === b.camera && a.trigger === b.trigger;

  // const handleSelectImage = (id: number) => {
  //   const foundItem = galleryImages.find((it) => it.id === id);
  //   if (!foundItem) return;
  //   const newPair = { camera: foundItem.camera, trigger: foundItem.trigger };
  //   console.log("THESE ARE THE CURRENT PAIRS:", selectedPairs);
  //   setSelectedPairs((prev) => {
  //     const alreadySelected = prev.some((p) => isSamePair(p, newPair));
  //     if (alreadySelected) {
  //       return prev.filter((p) => !isSamePair(p, newPair));
  //     } else {
  //       return [...prev, newPair];
  //     }
  //   });
  // };

  const handleGatherImages = async () => {
    console.log("Gathering images for pairs:", selectedPairs);
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teachmode/images/${outflowId}?system_name=${encodeURIComponent(system_name)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pairs: selectedPairs,
            job_start: jobStart,
            job_end: jobEnd,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.detail || "An unexpected error occurred.");
        throw new Error("Failed to gather images.");
      }
      const data = await response.json();
      navigate("/teachmode/annotate", {
        state: { imageDict: data.images, system_name, outflowId },
      });
    } catch (error) {
      console.error("Error gathering images:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedPairsString = selectedPairs
    .map((p) => `{${p.camera},${p.trigger}}`)
    .join("  ");

    const convertPairsToSelectedItems = (pairs: Array<{camera: string, trigger: string}>, galleryImages: CameraViewItem[]) => {
  return pairs.map(pair => {
    const matchingImage = galleryImages.find(img => 
      img.camera === pair.camera && img.trigger === pair.trigger
    );
    return {
      id: matchingImage?.id.toString() || '',
      system_name: matchingImage?.system_name || ''
    };
  }).filter(item => item.id !== ''); // Remove any that didn't match
};

const convertSelectedItemsToPairs = (selectedItems: Array<{id: string, system_name: string}>, galleryImages: CameraViewItem[]) => {
  return selectedItems.map(selected => {
    const matchingImage = galleryImages.find(img => 
      img.id.toString() === selected.id && (img.system_name || '') === selected.system_name
    );
    return matchingImage ? {
      camera: matchingImage.camera,
      trigger: matchingImage.trigger
    } : null;
  }).filter(pair => pair !== null) as Array<{camera: string, trigger: string}>;
};

// Your main component return statement should look like this:
return (
  <div className="gallery-container">
    <div className="info-box">
      <p>
        <strong>Outflow ID:</strong> {outflowId || "N/A"} <br />
        <strong>System Name:</strong> {system_name || "N/A"}<br />
        <strong>Total Outflow:</strong>{" "}
        {jobDatesLoading ? (
          <CircularProgress size={20} />
        ) : (
          totalOutflow || "N/A"
        )}
      </p>
      <br />
      {jobDatesLoading ? (
        <CircularProgress size={24} />
      ) : (
        <DateTimeRangePicker
          startDate={jobStart}
          setStartDate={setJobStart}
          endDate={jobEnd}
          setEndDate={setJobEnd}
        />
      )}
      <div style={{ marginTop: "2rem" }}>
        <strong>Selected Pairs:</strong>{" "}
        {selectedPairs.length > 0 ? selectedPairsString : "None"}
      </div>
      <Button
        color="blue"
        onClick={handleGatherImages}
        className="gather-images-button"
        disabled={loading || !!galleryError}
      >
        {loading ? (
          <>
            <CircularProgress
              size={24}
              color="inherit"
              style={{ marginRight: 8 }}
            />
            Gathering...
          </>
        ) : (
          "Gather Images"
        )}
      </Button>
    </div>

    {galleryError ? (
      <div className="empty-state-container" style={{ marginTop: "2rem" }}>
        <Empty
          image={<CameraOutlined style={{ fontSize: 64, color: '#bfbfbf' }} />}
          imageStyle={{ height: 80 }}
          description={
            <span className="empty-text">{galleryError}</span>
          }
        >
        </Empty>
      </div>
    ) : galleryLoading ? (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <CircularProgress size={40} />
      </div>
    ) : (
      <CardGallery
        data={galleryImages}
        itemsPerPage={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={(page: number, size?: number) => {
          setCurrentPage(page);
          if (size && size !== pageSize) {
            setPageSize(size);
            fetchGalleryImages(1, size);
          } else {
            fetchGalleryImages(page, pageSize);
          }
        }}
        cardClickPath=""
        CardComponent={CameraViewCard}
        // Enable selection mode
        selectionMode={true}
        // Convert your selectedPairs to the format CardGallery expects
        selectedItems={convertPairsToSelectedItems(selectedPairs, galleryImages)}
        // Handle selection changes
        onItemSelect={(id: string, system_name: string) => {
          const selectedItems = convertPairsToSelectedItems(selectedPairs, galleryImages);
          
          // Check if this item is already selected
          const isSelected = selectedItems.some(item => 
            item.id === id && item.system_name === system_name
          );
          
          let newSelectedItems;
          if (isSelected) {
            // Remove from selection
            newSelectedItems = selectedItems.filter(item => 
              !(item.id === id && item.system_name === system_name)
            );
          } else {
            // Add to selection
            newSelectedItems = [...selectedItems, { id, system_name }];
          }
          
          // Convert back to your pairs format
          const newSelectedPairs = convertSelectedItemsToPairs(newSelectedItems, galleryImages);
          setSelectedPairs(newSelectedPairs);
        }}
      />
    )}
  </div>
);
};

export default AddImagesCameraPage;
